import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Section } from '@/components/Section';
import { startCustomerPortal } from '@/lib/api';
import { LoginStep, type AuthSession } from './join/LoginStep';
import { loadAuthSession } from './join/authSessionStorage';

export function AccountPage() {
  const [params] = useSearchParams();
  const subscriptionId = params.get('subscription_id') ?? '';
  const [session, setSession] = useState<AuthSession | null>(() => loadAuthSession());
  const [email, setEmail] = useState(() => params.get('email') ?? session?.email ?? '');
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const openPortal = useCallback(async () => {
    if (!session || !subscriptionId || opening) return;
    setOpening(true);
    setError(null);
    try {
      const { url } = await startCustomerPortal({
        authToken: session.authToken,
        subscriptionId,
      });
      window.location.href = url;
    } catch {
      setError("Couldn't open your billing portal. Please try again or contact the gym.");
      setOpening(false);
    }
  }, [opening, session, subscriptionId]);

  useEffect(() => {
    if (!session || !subscriptionId) return;
    void openPortal();
  }, [openPortal, session, subscriptionId]);

  return (
    <Section eyebrow="Account" title="Manage membership.">
      {!subscriptionId ? (
        <div className="max-w-xl rounded-lg border border-bone/15 bg-neutral-900/60 p-5 text-sm text-bone/80">
          <p>This account link is missing a membership reference.</p>
          <Link to="/contact" className="btn-primary mt-4 inline-flex">
            Contact the gym
          </Link>
        </div>
      ) : (
        <div className="grid max-w-xl gap-5 rounded-lg border border-bone/15 bg-neutral-900/60 p-5 text-sm">
          {session ? (
            <>
              <p className="text-bone/70">
                Signed in as <strong className="text-bone">{session.email}</strong>.
              </p>
              <button
                type="button"
                className="btn-primary w-full sm:w-auto"
                onClick={() => void openPortal()}
                disabled={opening}
              >
                {opening ? 'Opening billing portal…' : 'Manage membership'}
              </button>
              {error && (
                <div
                  role="alert"
                  className="rounded border border-accent/50 bg-accent/10 p-3 text-accent"
                >
                  {error}
                </div>
              )}
            </>
          ) : (
            <>
              <p className="text-bone/70">
                Sign in with the email used for this membership to manage billing.
              </p>
              <LoginStep
                email={email}
                setEmail={setEmail}
                session={session}
                setSession={setSession}
              />
            </>
          )}
        </div>
      )}
    </Section>
  );
}
